// stores/vtdsStore.js
'use client';

import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payloadStr]) => {
  try {
    const payload = typeof payloadStr === 'string' ? JSON.parse(payloadStr) : payloadStr;
    const response = await fleetApi.post(url + getTzParam(), payload);
    return response;
  } catch (err) {
    console.error('API fetcher error:', { url, payloadStr, status: err?.response?.status, data: err?.response?.data });
    throw err;
  }
};

export const revalidateVtdsList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/vtds/search');
};

export const useVtdsList = (page = 0, rowsPerPage = 1000, searchKey = 'license_plate', searchValue = '', companyId = '') => {
  const start = page * rowsPerPage;

  const payload = useMemo(() => {
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .setOrder(['id'], false) // order by id asc: false (descending)
      .addJoin('company', ['name'])
      .addJoin('vehicle', ['license_plate']); // Join spesifik ke vehicle

    if (searchValue && searchValue.trim() !== '') {
      // Jika pencarian berdasarkan license_plate, arahkan ke table m_vehicle sesuai skema
      const tableName = searchKey === 'license_plate' ? 'm_vehicle' : null;
      fb.like(searchKey, searchValue, 'AND', tableName);
    }

    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany(); // Menggunakan company_id dari localStorage jika tidak ada param
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companyId]);

  const key = useMemo(() => ['/vtds/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  return {
    data: data?.data?.data ?? data?.data ?? [],
    totalRecord: data?.data?.totalRecord ?? data?.data?.totalRecords ?? data?.data?.dataSize ?? data?.totalRecord ?? 0,
    isLoading,
    isError: error
  };
};

export const getVtdsById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const resp = await fleetApi.get(`/vtds/${id}${getTzParam()}`);
    if (resp?.data) return resp.data;

    // Fallback search by ID jika struktur response get by id tidak langsung me-return object
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));
    const payload = fb.build();
    const response = await fleetApi.post(`/vtds/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    return Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
  } catch (err) {
    console.error('Failed to fetch vtds by id:', err);
    throw err;
  }
};

export const createVtds = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };
    const response = await fleetApi.post(`/vtds/new${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateVtdsList();
    return response.data;
  } catch (err) {
    console.error('Error creating vtds:', err);
    throw err;
  }
};

export const updateVtds = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      ...formData
    };
    const response = await fleetApi.put(`/vtds/edit/${id}${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }

    revalidateVtdsList();
    mutate(`/vtds/${id}`); // Update spesifik cache ID jika digunakan di halaman detail
    return response.data;
  } catch (err) {
    console.error('Error updating vtds:', err);
    throw err;
  }
};

export const deleteVtds = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/vtds/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateVtdsList();
    return response.data;
  } catch (err) {
    console.error('Error deleting vtds:', err);
    throw err;
  }
};
